import { isEmpty, isNil } from 'lodash'

import {
  CATEGORIZATION_MAX_OPTIONS,
  PICK_LIST_MAX_OPTIONS,
  maxOptionsFor,
  readOptionsCsv,
} from '@/components/forms/OptionsEditor/OptionsEditor.helpers'

export { CATEGORIZATION_MAX_OPTIONS, PICK_LIST_MAX_OPTIONS, maxOptionsFor, readOptionsCsv }

// Snapshot an options editor's current state as { root, options } — the shape
// saveTaxonomyOptions takes and that buildInlineTaxonomies converts for a test
// run. Shared by the pick_list and categorization editors. `root` is the loaded
// taxonomy (present once the editor has finished loading); each option keeps its
// uuid (null for newly added) so a later save reuses it.
export const buildTaxonomyEdit = (root, options) => ({
  root,
  options: options.map((option) => ({
    taxonomy_uuid: option.taxonomy_uuid ?? null,
    name: option.name.trim(),
    description: (option.description ?? '').trim(),
  })),
})

// Convert the editor's pending taxonomy edits ({ [uuid]: { root, options } })
// into the /bit-flows/test `inline_taxonomies` shape ({ [uuid]: { name,
// description, sub_categories: [{ taxonomy_uuid, name, description,
// sub_categories: [] }] } }).
//
// An option that already exists carries its uuid through. Without it every
// option in the edit reads as brand new, so a run gated on any of them fails
// with "gates on category <uuid>, which is not an option of the taxonomy" — and
// the uuid named is whichever edge was checked first, not anything the author
// touched. A newly added option has no uuid to carry.
export const buildInlineTaxonomies = (pendingTaxonomies = {}) =>
  Object.entries(pendingTaxonomies).reduce((acc, [taxonomyUuid, { root, options }]) => {
    acc[taxonomyUuid] = {
      name: root?.name ?? '',
      description: root?.description ?? '',
      sub_categories: (options ?? []).map((option) => ({
        ...(isNil(option.taxonomy_uuid) ? {} : { taxonomy_uuid: option.taxonomy_uuid }),
        name: option.name,
        description: option.description ?? '',
        sub_categories: [],
      })),
    }
    return acc
  }, {})

// Normalize a field key for uniqueness checks — a field name is "the same"
// regardless of surrounding whitespace or casing.
export const normalizeFieldKey = (key) => (key ?? '').trim().toLowerCase()

// Returns the normalized field keys that appear on more than one row, as a Set
// so per-row membership checks stay O(1). Empty keys are ignored (blank new
// rows aren't duplicates of each other).
export const getDuplicateFieldKeys = (promptFields = []) => {
  const counts = Object.create(null)
  promptFields.forEach((field) => {
    const key = normalizeFieldKey(field?.key)
    if (key) {
      counts[key] = (counts[key] ?? 0) + 1
    }
  })
  return new Set(Object.keys(counts).filter((key) => counts[key] > 1))
}

// Count how many rows use each field key. Null-prototype so user-entered keys
// like "constructor" or "toString" can't collide with Object.prototype.
export const buildKeyUsageCounts = (promptFields = []) => {
  const counts = Object.create(null)
  promptFields.forEach((field) => {
    if (field?.key) {
      counts[field.key] = (counts[field.key] ?? 0) + 1
    }
  })
  return counts
}

// A field's UI "type" is its bit field_type combined with its data_type:
// pick_list and categorization both read as "Pick List"; extraction fields
// split by data_type into Text / Number / Yes-No. Each maps to a display label
// and Pill color props. Shared by the field-type pills and the Add Field
// modal's type controls / name gating.
export const FIELD_TYPE_META = {
  // Cyan has no Pill semantic variant, so pick_list is styled via a modifier
  // class (see rtn-field-type-pill--picklist) rather than pillProps.
  pick_list: { label: 'Pick List', pillProps: {} },
  string: { label: 'Text', pillProps: { brand: true } },
  integer: { label: 'Number', pillProps: { caution: true } },
  boolean: { label: 'Yes/No', pillProps: {} },
  // Display only. A logic field's answer is a Yes/No, so everything that compares
  // types (name reuse, condition operators, branches) keeps reading it as
  // boolean; the pill alone says Logic, since that's how the list tells a field
  // built from conditions apart from a prompted Yes/No.
  logic: { label: 'Logic', pillProps: { informative: true } },
}

// A logic field runs no prompt: it combines the conditions on its incoming
// edges into a stored Yes/No. Its description is the combinator, not prose —
// 'all' (every condition holds) or 'any' (at least one does) — which is what
// the summary service reads.
export const LOGIC_FIELD_TYPE = 'logic'

// The FIELD_TYPE_META key a logic field's pill reads as (and only its pill).
export const LOGIC_TYPE_KEY = 'logic'

// The FIELD_TYPE_META key every option-backed field reads as.
export const PICK_LIST_TYPE_KEY = 'pick_list'

// The key a Yes/No field reads as. Named because three places branch on it and
// they have to agree.
export const BOOLEAN_TYPE_KEY = 'boolean'

// Collapse a field's (field_type, data_type) to a FIELD_TYPE_META key. Both
// list bit types share the pick_list key; every other field type (extraction,
// voip_metadata) falls through to its data_type.
//
// float has no key of its own — it reads as a Number, matching the backend's
// FieldDisplayType, which maps both numeric data types to Numeric. So an integer
// extraction field and a float metadata field are one type for name-reuse
// purposes; that's forced rather than chosen, since the org Fields API exposes
// only display_type and a Numeric name can't be told apart from either side.
// Reuse never rewrites a field's data_type — the save path sends the row's own.
export const resolveFieldTypeKey = (fieldType, dataType) => {
  if (fieldType === 'pick_list' || fieldType === 'categorization') {
    return PICK_LIST_TYPE_KEY
  }
  // A logic field's answer is always a Yes/No, so it compares as one even on a
  // row that never carried its data_type.
  if (fieldType === LOGIC_FIELD_TYPE) {
    return BOOLEAN_TYPE_KEY
  }
  if (dataType === 'float') {
    return 'integer'
  }
  return dataType
}

export const getFieldTypeMeta = (fieldType, dataType) =>
  FIELD_TYPE_META[resolveFieldTypeKey(fieldType, dataType)]

// The summary service now hands us an authoritative user-facing type
// (FieldDisplayType) on field responses, so we no longer reconstruct it from
// (field_type, data_type). Map those wire values onto the same FIELD_TYPE_META
// keys the (field_type, data_type) path resolves to, so a display_type and a
// derived type compare/render identically. Notably the org Fields API can't
// derive pick_list on its own (it exposes no field_type), which is why the
// backend computes this — see the TREN-377 display-type work.
export const DISPLAY_TYPE_META_KEY = {
  Text: 'string',
  Numeric: 'integer',
  'Yes/No': 'boolean',
  Picklist: 'pick_list',
}

export const resolveDisplayTypeKey = (displayType) => DISPLAY_TYPE_META_KEY[displayType]

export const getDisplayTypeMeta = (displayType) =>
  FIELD_TYPE_META[resolveDisplayTypeKey(displayType)]

export const getDisplayTypeLabel = (displayType) => getDisplayTypeMeta(displayType)?.label

export const LOGIC_COMBINATOR = {
  ALL: 'all',
  ANY: 'any',
}

// What each combinator does, worded for someone reading the row rather than
// the service. `joiner` is the word drawn between that field's conditions.
export const LOGIC_COMBINATOR_COPY = {
  [LOGIC_COMBINATOR.ALL]: {
    label: 'All',
    joiner: 'and',
    explanation: 'Yes only when every condition is met. No as soon as one is not.',
  },
  [LOGIC_COMBINATOR.ANY]: {
    label: 'Any',
    joiner: 'or',
    explanation: 'Yes when at least one condition is met. No only when none are.',
  },
}

// How an input with no answer is read, stated once wherever the combinator is.
export const LOGIC_MISSING_INPUT_NOTE =
  'A condition on a field that was skipped only holds for "has no value". If a field failed and its answer could change the result, this field is skipped too.'

export const isLogicField = (promptField = {}) => promptField.field_type === LOGIC_FIELD_TYPE

// The logic fields with nothing to combine. The service rejects a save with one,
// since a logic field's conditions are its whole definition.
export const findLogicFieldsWithoutConditions = (promptFields = [], links = []) =>
  promptFields.filter(
    (field) =>
      isLogicField(field) &&
      !links.some(
        (link) =>
          normalizeFieldKey(link.target) === normalizeFieldKey(field.key) && !isNil(link.operator)
      )
  )

// Whether a taxonomy drives a voip_metadata field — i.e. it was given the
// Pick List data type, so its value resolves against options. A saved one
// carries the taxonomy_uuid; an unsaved one still has its options staged on the
// row. Such a field reads as a Pick List despite its string data_type.
export const isTaxonomyDrivenMetadataField = (promptField = {}) =>
  promptField.field_type === 'voip_metadata' &&
  (!isNil(promptField.taxonomy_uuid) || !isEmpty(promptField.new_taxonomy_options))

// The org default fields keyed by normalized name (a field name is its identity
// org-wide). Answers two things the API won't: what a name already comes with at
// the org level — its type, description and options — so creating a field under
// that name doesn't reinvent them; and which taxonomy a row would return to,
// since inherited_attributes reports what a field does inherit, not what it
// could.
export const getDefaultFieldsByFieldName = (orgDefaultFlow) => {
  const byName = {}
  if (isEmpty(orgDefaultFlow?.fields)) {
    return byName
  }
  orgDefaultFlow.fields.forEach((field) => {
    if (!isEmpty(field.field_name)) {
      byName[normalizeFieldKey(field.field_name)] = field
    }
  })
  return byName
}

// Whether a row's options changed in a way only the configuration save can carry:
// it points at a different taxonomy than the loaded flow has for it, or at none
// because a copy is staged in its place. Switching between the org default's
// options and a copy of its own is an options change like any other, so the row
// marks it and the save asks about it the same way an unsaved option edit does.
export const hasSwitchedTaxonomy = (promptField, savedField) =>
  !isNil(savedField) && (savedField.taxonomy_uuid ?? null) !== (promptField.taxonomy_uuid ?? null)

// Whether a row's options are the org default's, shared with every playbook that
// inherits the field. Which taxonomy the row points at settles it whenever the
// default's is known: pointing at the default's means sharing however the row got
// there, and holding a different one means the options are this configuration's
// whatever the flow reported — a field can be listed as inheriting the attribute
// while overriding its value. Only with nothing to compare against (defaults not
// loaded, or no default under this name) does the flow's own answer stand. In the
// defaults editor a row IS that default, so pointing at its own taxonomy isn't
// sharing.
export const inheritsTaxonomyFromDefault = (
  promptField,
  defaultFieldsByName = {},
  isOrgDefaultFlow = false
) => {
  const defaultTaxonomyUuid =
    defaultFieldsByName[normalizeFieldKey(promptField?.key)]?.taxonomy_uuid
  if (!isOrgDefaultFlow && !isNil(defaultTaxonomyUuid)) {
    return defaultTaxonomyUuid === promptField?.taxonomy_uuid
  }
  return promptField?.inheritsTaxonomy === true
}

// The rows in that state, for a save that has to name how many fields it covers.
export const countSwitchedTaxonomies = (promptFields = [], flowFields = []) => {
  const savedByFieldId = {}
  flowFields.forEach((field) => {
    savedByFieldId[field.field_id] = field
  })
  return promptFields.filter((field) => hasSwitchedTaxonomy(field, savedByFieldId[field.field_id]))
    .length
}

// A row's own display-type key. Reusing an existing org field name is same-type
// only — changing a field's type is done by creating a new field, not via
// reuse — so a row gates its name options against this. A blank/new row carries
// no explicit type and is an extraction/text field until saved, so it defaults
// to Text.
export const getRowTypeKey = (promptField = {}) => {
  if (isTaxonomyDrivenMetadataField(promptField)) {
    return PICK_LIST_TYPE_KEY
  }
  return resolveFieldTypeKey(promptField.field_type, promptField.data_type || 'string')
}

// The display label for a FIELD_TYPE_META key (e.g. 'string' -> 'Text',
// 'pick_list' -> 'Pick List') — used to name a row's type in copy.
export const getTypeKeyLabel = (typeKey) => FIELD_TYPE_META[typeKey]?.label

// Whether an org field-name option can't be reused on a row of rowTypeKey: its
// display type differs. Options with no display type (free text, the current
// value) are never blocked.
export const isReuseTypeMismatch = (option, rowTypeKey) =>
  !isNil(option?.displayType) && resolveDisplayTypeKey(option.displayType) !== rowTypeKey

// Org field names still available to a row: those used by no OTHER row. A name
// used only by the current row (via currentKey) stays available; a name shared
// by another row (duplicate key) is excluded. fieldNames are { name, displayType }
// (the display type lets the caller show a pill and gate by type);
// keyUsageCounts comes from buildKeyUsageCounts.
export const getAvailableFieldNames = (fieldNames = [], keyUsageCounts, currentKey) =>
  fieldNames
    .filter(
      (field) => (keyUsageCounts?.[field.name] ?? 0) - (field.name === currentKey ? 1 : 0) === 0
    )
    .map((field) => ({
      value: field.name,
      label: field.name,
      displayType: field.displayType,
    }))

// Extraction prompts must reference both the bit JSON payload and the transcript.
// Each token only needs to appear in one of the system/user prompts to be valid.
export const REQUIRED_PROMPT_TOKENS = ['$bit_json', '$transcript']

// The required tokens not present in either prompt. Joins with a newline so a
// token can't be matched across the system/user boundary.
export const getMissingPromptTokens = (systemPrompt, userPrompt) => {
  const combined = `${systemPrompt ?? ''}\n${userPrompt ?? ''}`
  return REQUIRED_PROMPT_TOKENS.filter((token) => !combined.includes(token))
}

// Whether any admin-editable prompt in the form is missing a required token,
// which blocks save. The shared prompt is only checked when it's backed by a
// persistable record (sharedRequired); each individual-execution field owns its
// own prompt and is always checked.
export const hasPromptMissingRequiredTokens = (values, { sharedRequired = false } = {}) => {
  if (
    sharedRequired &&
    !isEmpty(getMissingPromptTokens(values.system_prompt, values.user_prompt))
  ) {
    return true
  }
  return (values.prompt_fields ?? []).some(
    (field) =>
      field.individualExecution &&
      !isEmpty(getMissingPromptTokens(field.system_prompt, field.user_prompt))
  )
}

export const MAX_OPTION_LENGTH = 60

// Why an option set can't be used yet, or '' when it's valid — checked
// most-blocking first so the caller can surface a specific message. Shared by
// the create modal and the pre-save staged-options editor.
export const getOptionsError = (options, isCategorization) => {
  if (isEmpty(options)) {
    return 'Add at least one option'
  }
  if (options.length > maxOptionsFor(isCategorization)) {
    return `A ${isCategorization ? 'categorization' : 'pick list'} field can have at most ${maxOptionsFor(isCategorization)} options`
  }
  if (options.some((option) => isEmpty(option.name.trim()))) {
    return 'Every option needs a name'
  }
  if (options.some((option) => option.name.trim().length > MAX_OPTION_LENGTH)) {
    return `Option names must be ${MAX_OPTION_LENGTH} characters or less`
  }
  if (isCategorization && options.some((option) => isEmpty((option.description ?? '').trim()))) {
    return 'Every option needs a description'
  }
  return ''
}

// Why a set of rows fails the value check, for the Save button's tooltip. A
// voip_metadata row's value is the metadata path it reads rather than prose, so
// an all-metadata set is named for what it's missing; any other mix names the
// common case.
export const getInvalidValueMessage = (invalidFields) =>
  invalidFields.every((field) => field.field_type === 'voip_metadata')
    ? 'Every metadata field needs a path under 5000 characters'
    : 'Every field needs a description under 5000 characters'

export const buildPrompt = (values) => {
  const promptHeadline = `This is a transcript of a call between an agent and a customer. I would like you to summarize it and give me ${values?.length} things back.\n\n`
  const promptBody = values
    .map((field) => {
      // regex below removes all newlines in the value field and replaces them with spaces, then trims the input
      return `${field.key}: ${field.value.replace(/\r?\n|\r/g, ' ').trim()}\n`
    })
    .join('')
  const promptFooter = '\nhere is the transcript: $transcript'
  return `${promptHeadline}${promptBody}${promptFooter}`
}
